(() => {
  const cache = new Map();
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  function decode(value, Type) {
    if (Array.isArray(value)) return new Type(value);
    const raw = atob(value);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    return new Type(bytes.buffer);
  }
  async function loadMesh(url) {
    if (!cache.has(url)) {
      const task = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 20000);
        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
          if (!response.ok) throw Error('Model unavailable');
          return await response.json();
        } finally { clearTimeout(timer); }
      })();
      cache.set(url, task);
      task.catch(() => cache.delete(url));
    }
    return cache.get(url);
  }
  function createRenderer(canvas, onError) {
    const gl = canvas.getContext('webgl', { antialias: true, alpha: true, powerPreference: 'low-power' });
    if (!gl) throw Error('WebGL unavailable');
    const vs = 'attribute vec3 position;attribute vec3 normal;uniform mat3 rotation;uniform float scale;uniform float aspect;varying vec3 N;varying vec3 P;void main(){P=rotation*position;N=rotation*normal;gl_Position=vec4(P.x*scale/aspect,P.y*scale,-P.z*0.35,1.0);}';
    const fs = 'precision mediump float;varying vec3 N;varying vec3 P;void main(){vec3 n=normalize(N);if(!gl_FrontFacing)n=-n;vec3 light=normalize(vec3(-0.5,0.8,1.2));float diffuse=max(dot(n,light),0.0);float fill=max(dot(n,normalize(vec3(0.9,-0.1,0.6))),0.0);float spec=pow(max(dot(n,normalize(light+vec3(0.0,0.0,1.0))),0.0),38.0);vec3 c=vec3(0.72,0.725,0.73)*(0.43+0.5*diffuse+0.15*fill)+vec3(0.13)*spec;gl_FragColor=vec4(c,1.0);}';
    const shaders = [];
    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source); gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw Error('Shader unavailable');
      shaders.push(shader); return shader;
    };
    const program = gl.createProgram();
    gl.attachShader(program, compile(gl.VERTEX_SHADER, vs));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw Error('Renderer unavailable');
    const locations = { position: gl.getAttribLocation(program, 'position'), normal: gl.getAttribLocation(program, 'normal'), rotation: gl.getUniformLocation(program, 'rotation'), scale: gl.getUniformLocation(program, 'scale'), aspect: gl.getUniformLocation(program, 'aspect') };
    const buffers = { position: gl.createBuffer(), normal: gl.createBuffer(), index: gl.createBuffer() };
    let count = 0, type = gl.UNSIGNED_SHORT, yaw = -0.25, pitch = 0.12, zoom = 1, radius = 1, request = 0, active = true;
    const pointers = new Map();
    let pinch = 0;
    const events = new AbortController();
    const listen = (node, type, handler, options = {}) => node.addEventListener(type, handler, { ...options, signal: events.signal });
    function draw() {
      request = 0;
      if (!active || !count || gl.isContextLost()) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.round(rect.width * dpr), h = Math.round(rect.height * dpr);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      const cy = Math.cos(yaw), sy = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch);
      const rotation = new Float32Array([cy, sp * sy, -cp * sy, 0, cp, sp, sy, -sp * cy, cp * cy]);
      gl.viewport(0, 0, w, h); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE); gl.useProgram(program);
      for (const key of ['position', 'normal']) {
        gl.bindBuffer(gl.ARRAY_BUFFER, buffers[key]); gl.enableVertexAttribArray(locations[key]);
        gl.vertexAttribPointer(locations[key], 3, gl.FLOAT, false, 0, 0);
      }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.index);
      gl.uniformMatrix3fv(locations.rotation, false, rotation);
      gl.uniform1f(locations.aspect, w / h);
      gl.uniform1f(locations.scale, Math.min(1, w / h) * 0.84 * zoom / radius);
      gl.drawElements(gl.TRIANGLES, count, type, 0);
      canvas.dataset.yaw = String(Math.round(yaw * 180 / Math.PI));
      canvas.dataset.pitch = String(Math.round(pitch * 180 / Math.PI));
      canvas.dataset.zoom = zoom.toFixed(2);
    }
    const render = () => { if (!request) request = requestAnimationFrame(draw); };
    const zoomBy = (factor) => { zoom = clamp(zoom * factor, 0.65, 3); render(); };
    const view = (name) => {
      if (name === 'front') { yaw = 0; pitch = 0; }
      else if (name === 'back') { yaw = Math.PI; pitch = 0; }
      else if (name === 'side') { yaw = -Math.PI / 2; pitch = 0; }
      else if (name === 'left') yaw -= Math.PI / 4;
      else if (name === 'right') yaw += Math.PI / 4;
      else { yaw = -0.25; pitch = 0.12; zoom = 1; }
      render();
    };
    listen(canvas, 'pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointers.set(event.pointerId, [event.clientX, event.clientY]);
      canvas.setPointerCapture(event.pointerId);
      canvas.focus({ preventScroll: true });
      if (pointers.size === 2) {
        const p = [...pointers.values()]; pinch = Math.hypot(p[0][0] - p[1][0], p[0][1] - p[1][1]);
      }
    });
    listen(canvas, 'pointermove', (event) => {
      const previous = pointers.get(event.pointerId);
      if (!previous) return;
      pointers.set(event.pointerId, [event.clientX, event.clientY]);
      if (pointers.size === 2) {
        const p = [...pointers.values()], distance = Math.hypot(p[0][0] - p[1][0], p[0][1] - p[1][1]);
        if (pinch > 0) zoomBy(distance / pinch);
        pinch = distance;
      } else if (pointers.size === 1) {
        yaw += (event.clientX - previous[0]) * 0.012;
        pitch = clamp(pitch + (event.clientY - previous[1]) * 0.012, -Math.PI / 2, Math.PI / 2);
        render();
      }
    });
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) listen(canvas, type, event => { pointers.delete(event.pointerId); pinch = 0; });
    listen(canvas, 'wheel', (event) => { event.preventDefault(); zoomBy(Math.exp(-event.deltaY * 0.0015)); }, { passive: false });
    listen(canvas, 'keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '+', '=', '-', 'Home'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === '+ ' || event.key === '+' || event.key === '=') zoomBy(1.2);
      else if (event.key === '-') zoomBy(1 / 1.2);
      else if (event.key === 'Home') view('reset');
      else { yaw += event.key === 'ArrowLeft' ? -0.16 : event.key === 'ArrowRight' ? 0.16 : 0; pitch = clamp(pitch + (event.key === 'ArrowUp' ? -0.16 : event.key === 'ArrowDown' ? 0.16 : 0), -Math.PI / 2, Math.PI / 2); render(); }
    });
    listen(canvas, 'webglcontextlost', event => { event.preventDefault(); active = false; onError(); });
    const observer = new ResizeObserver(render); observer.observe(canvas);
    return {
      setMesh(data) {
        let positions = decode(data.positions, Float32Array);
        const normals = decode(data.normals, Float32Array);
        const indices = decode(data.indices, data.indexType === 'uint32' ? Uint32Array : Uint16Array);
        if (!positions.length || positions.length !== normals.length || !indices.length) throw Error('Invalid model');
        type = indices instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
        if (type === gl.UNSIGNED_INT && !gl.getExtension('OES_element_index_uint')) throw Error('Unsupported model');
        const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
        for (let i = 0; i < positions.length; i++) { const axis = i % 3; if (!Number.isFinite(positions[i])) throw Error('Invalid geometry'); min[axis] = Math.min(min[axis], positions[i]); max[axis] = Math.max(max[axis], positions[i]); }
        const center = min.map((v, i) => (v + max[i]) / 2);
        const span = Math.max(...max.map((v, i) => v - min[i]));
        if (!(span > 0)) throw Error('Empty model');
        positions = positions.map((v, i) => (v - center[i % 3]) * 2 / span);
        radius = 0;
        for (let i = 0; i < positions.length; i += 3) radius = Math.max(radius, Math.hypot(positions[i], positions[i + 1], positions[i + 2]));
        for (const [key, array] of [['position', positions], ['normal', normals]]) { gl.bindBuffer(gl.ARRAY_BUFFER, buffers[key]); gl.bufferData(gl.ARRAY_BUFFER, array, gl.STATIC_DRAW); }
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.index); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
        count = indices.length; view('reset');
      },
      view, zoomBy,
      destroy() { active = false; cancelAnimationFrame(request); events.abort(); observer.disconnect(); for (const buffer of Object.values(buffers)) gl.deleteBuffer(buffer); for (const shader of shaders) gl.deleteShader(shader); gl.deleteProgram(program); gl.getExtension('WEBGL_lose_context')?.loseContext(); }
    };
  }
  function init(root) {
    if (root.dataset.mrfNailsReady) return;
    root.dataset.mrfNailsReady = 'true';
    const dialog = root.querySelector('[data-mrf-nail-dialog]');
    if (!dialog) return;
    const text = JSON.parse(dialog.querySelector('[data-mrf-nail-messages]').textContent);
    root.querySelectorAll('[data-mrf-nail-enhancement]').forEach(enhancement => {
      const form = enhancement.closest('form');
      const fallback = form.querySelector('[data-mrf-nail-fallback]');
      const selects = [...fallback.querySelectorAll('[data-mrf-nail-select]')];
      const choices = [...enhancement.querySelectorAll('[data-mrf-nail-choice]')];
      const count = enhancement.querySelector('[data-mrf-nail-count]');
      const sync = () => {
        const values = selects.map(x => x.value).filter(Boolean);
        choices.forEach(choice => { choice.checked = values.includes(choice.value); choice.disabled = !choice.checked && values.length >= 2; });
        count.textContent = text.selected_count.replace('{{ count }}', String(values.length));
      };
      choices.forEach(choice => choice.addEventListener('change', () => {
        const selected = choices.filter(x => x.checked).map(x => x.value);
        selects.forEach((select, index) => { select.value = selected[index] || ''; });
        selects[0].dispatchEvent(new Event('change', { bubbles: true }));
        sync();
      }));
      selects.forEach(select => { select.required = false; select.addEventListener('change', sync); });
      sync(); fallback.hidden = true; enhancement.hidden = false;
    });
    const stage = dialog.querySelector('[data-mrf-nail-stage]');
    const poster = dialog.querySelector('[data-mrf-nail-poster]');
    const status = dialog.querySelector('[data-mrf-nail-status]');
    const controls = dialog.querySelector('[data-mrf-nail-controls]');
    let renderer, canvas, generation = 0, opener, oldOverflow;
    const failure = () => {
      dialog.dataset.state = 'error'; status.textContent = text.error; status.hidden = false;
      if (canvas) canvas.hidden = true;
      poster.hidden = false; controls.hidden = true;
    };
    root.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[data-mrf-open-nail]');
      if (!trigger || !root.contains(trigger)) return;
      const thisGeneration = ++generation;
      opener = trigger;
      dialog.querySelector('[data-mrf-nail-title]').textContent = trigger.dataset.nailLabel || trigger.dataset.modelLabel;
      const size = trigger.closest('[data-mrf-card]')?.querySelector('[data-mrf-variant]:checked')?.dataset.size || 'L';
      dialog.querySelector('[data-mrf-nail-size]').textContent = text.size.replace('{{ size }}', size);
      dialog.dataset.modelKey = trigger.dataset.mrfOpenNail;
      dialog.dataset.state = 'loading';
      poster.src = trigger.dataset.modelPoster || trigger.closest('[data-mrf-card]')?.querySelector('img')?.src || '';
      poster.alt = trigger.dataset.nailLabel || trigger.dataset.modelLabel;
      poster.hidden = false; controls.hidden = true; status.hidden = false; status.textContent = text.loading;
      oldOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
      if (!dialog.open) dialog.showModal();
      try {
        const mesh = await loadMesh(trigger.dataset.modelUrl);
        if (thisGeneration !== generation || !dialog.open) return;
        canvas = document.createElement('canvas'); canvas.tabIndex = 0;
        canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', text.canvas_label.replace('{{ name }}', poster.alt));
        canvas.setAttribute('aria-describedby', dialog.querySelector('[data-mrf-nail-instruction]').id);
        canvas.setAttribute('data-mrf-nail-canvas', ''); stage.append(canvas);
        renderer = createRenderer(canvas, failure); renderer.setMesh(mesh);
        poster.hidden = true; status.hidden = true; controls.hidden = false; dialog.dataset.state = 'ready';
      } catch (_) { if (thisGeneration === generation && dialog.open) failure(); }
    });
    dialog.querySelector('[data-mrf-nail-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } });
    dialog.addEventListener('close', () => {
      generation++; renderer?.destroy(); renderer = null; canvas?.remove(); canvas = null;
      document.body.style.overflow = oldOverflow || ''; opener?.focus({ preventScroll: true });
    });
    dialog.querySelectorAll('[data-mrf-nail-view]').forEach(button => button.addEventListener('click', () => renderer?.view(button.dataset.mrfNailView)));
    dialog.querySelectorAll('[data-mrf-nail-zoom]').forEach(button => button.addEventListener('click', () => renderer?.zoomBy(Number(button.dataset.mrfNailZoom))));
  }
  const initAll = () => document.querySelectorAll('[data-mrf-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
